import { useRef } from 'react';

// Radix menus and selects hand focus back to their trigger on close. Chrome can paint that as
// keyboard focus (:focus-visible) even when the user closed the popup with the mouse, leaving a
// focus ring on the trigger nobody asked for. This keeps the hand-back for keyboard closes (Escape,
// Enter on an item) and skips it after a mouse close (click outside, or click/drag-release on an
// item). The last interaction decides: a keypress after a click counts as a keyboard close.
//
// Pass the caller's own handlers through; returns the props to spread on the Radix Content.
export const useKeyboardOnlyReturnFocus = ({
  onPointerDown,
  onPointerUp,
  onKeyDown,
  onEscapeKeyDown,
  onPointerDownOutside,
  onCloseAutoFocus,
}) => {
  const closedByPointer = useRef(false);
  const markPointer = (handler) => (event) => {
    closedByPointer.current = true;
    handler?.(event);
  };

  return {
    onPointerDown: markPointer(onPointerDown),
    onPointerUp: markPointer(onPointerUp),
    onPointerDownOutside: markPointer(onPointerDownOutside),
    onKeyDown: (event) => {
      closedByPointer.current = false;
      onKeyDown?.(event);
    },
    // Radix calls this before it closes, so Escape always counts as a keyboard close — onKeyDown
    // alone can run after the close has already been handled.
    onEscapeKeyDown: (event) => {
      closedByPointer.current = false;
      onEscapeKeyDown?.(event);
    },
    onCloseAutoFocus: (event) => {
      onCloseAutoFocus?.(event);
      if (closedByPointer.current) event.preventDefault();
      closedByPointer.current = false;
    },
  };
};
